// src/components/ui/AppTile.tsx — tile button used by the Apps grid and the
// Home quick-links section.
//
// Port of docs/prototype/shell.jsx 47-75 with the audit-resolved overrides:
//   - Brass top-edge gradient is uniform across all tiles (matches the Card
//     primitive's edge prop; the prototype's per-tone stripe was dropped to
//     reduce visual noise).
//   - The icon disc remains per-tone so the tone palette still reads.
//
// iconKey is a string lookup into the Icon module so non-React callers
// (apps registry, server-side rendering) can store the key without holding
// a React component reference.
//
// Pin control (2026-11-04): when `onPin` is given the tile grows a 44px pin
// button in its top-right corner (a SIBLING of the launch button, never nested
// -- nested buttons are invalid HTML). `pinned` fills it. Always visible, no
// hover needed, so it works on touch.

import type { CSSProperties, ReactElement } from "react";
import { C, F } from "../../theme";
import * as Icons from "./Icon";
import type { IconProps } from "./Icon";

type IconComponent = (props: IconProps) => ReactElement;

function iconFor(key: string): IconComponent | null {
  const entry = (Icons as unknown as Record<string, unknown>)[key];
  return typeof entry === "function" ? (entry as IconComponent) : null;
}

export type AppTileTone = "brass" | "heather" | "sage" | "signal" | "paper";
export type AppTileSize = "lg" | "sm";

export interface AppTileProps {
  name: string;
  desc: string;
  iconKey: string;
  tone?: AppTileTone;
  size?: AppTileSize;
  onTap: () => void;
  /** Pin control. When set, a pin button renders top-right. */
  onPin?: () => void;
  pinned?: boolean;
  /** Disables the pin button (e.g. bay full and this app is not pinned). */
  pinDisabled?: boolean;
}

interface ToneStyle {
  bg: string;
  ic: string;
}

const TONES: Record<AppTileTone, ToneStyle> = {
  brass:   { bg: C.brassBg,   ic: C.brassDk   },
  heather: { bg: C.heatherBg, ic: C.heatherDk },
  sage:    { bg: C.sageBg,    ic: C.sageDk    },
  signal:  { bg: C.signalBg,  ic: C.signalDk  },
  paper:   { bg: C.paperWarm, ic: C.brassDk   },
};

export const AppTile = ({
  name,
  desc,
  iconKey,
  tone = "brass",
  size = "lg",
  onTap,
  onPin,
  pinned = false,
  pinDisabled = false,
}: AppTileProps) => {
  const t = TONES[tone];
  const Icon = iconFor(iconKey);
  const compact = size === "sm";
  const hasPin = typeof onPin === "function";

  const buttonStyle: CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    justifyContent: "flex-start",
    background: C.card,
    border: `1px solid ${pinned ? C.brassLt : C.hairlineLt}`,
    borderRadius: 18,
    padding: compact ? "12px 12px" : "16px 14px",
    // Leave room for the pin button so the title never runs under it.
    paddingRight: hasPin ? (compact ? 44 : 52) : undefined,
    minHeight: compact ? 92 : 128,
    cursor: "pointer",
    textAlign: "left",
    boxShadow: C.shadowS,
    fontFamily: F.body,
    WebkitTapHighlightColor: "transparent",
    touchAction: "manipulation",
    position: "relative",
    overflow: "hidden",
    width: "100%",
    height: "100%",
    color: C.ink,
  };

  return (
    <div style={{ position: "relative", height: "100%" }}>
      <button type="button" onClick={onTap} style={buttonStyle}>
        {/* Uniform brass top-edge stripe (audit override) */}
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 18,
            right: 18,
            height: 2,
            background: `linear-gradient(90deg, transparent, ${C.brassLt} 20%, ${C.brassLt} 80%, transparent)`,
            opacity: 0.5,
            pointerEvents: "none",
          }}
          aria-hidden="true"
        />

        <div
          style={{
            width: compact ? 40 : 48,
            height: compact ? 40 : 48,
            borderRadius: 13,
            background: t.bg,
            border: `1px solid ${C.hairlineLt}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: compact ? 8 : 12,
          }}
        >
          {Icon && <Icon s={compact ? 20 : 24} c={t.ic} />}
        </div>

        <div
          style={{
            fontFamily: F.serif,
            fontSize: compact ? 15 : 17,
            fontWeight: 700,
            color: C.ink,
            lineHeight: 1.15,
            letterSpacing: -0.1,
          }}
        >
          {name}
        </div>
        <div
          style={{
            fontSize: compact ? 12 : 13,
            color: C.inkSoft,
            marginTop: 3,
            fontWeight: 600,
            lineHeight: 1.3,
          }}
        >
          {desc}
        </div>
      </button>

      {hasPin && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!pinDisabled) onPin();
          }}
          aria-label={pinned ? `Unpin ${name}` : `Pin ${name}`}
          aria-pressed={pinned}
          title={pinned ? "Unpin from your bay" : pinDisabled ? "Pin bay is full" : "Pin to your bay"}
          disabled={pinDisabled && !pinned}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: compact ? 36 : 44,
            height: compact ? 36 : 44,
            borderRadius: 12,
            border: `1px solid ${pinned ? C.brassLt : C.hairlineLt}`,
            background: pinned ? C.brassBg : C.card,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: pinDisabled && !pinned ? "not-allowed" : "pointer",
            opacity: pinDisabled && !pinned ? 0.4 : 1,
            WebkitTapHighlightColor: "transparent",
            touchAction: "manipulation",
            padding: 0,
          }}
        >
          <Icons.Pin s={compact ? 16 : 18} c={pinned ? C.brassDk : C.inkMute} fill={pinned} />
        </button>
      )}
    </div>
  );
};
